import { useQuery } from '@tanstack/react-query';
import { TrendingUp, ClipboardList, Coins, Award, Users } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

/**
 * Fundraising pipeline KPI summary for the admin home dashboard.
 *
 * Mirrors GET /admin/fundraising/dashboard. Numeric SUM columns come back from
 * postgres.js as strings, so money fields are typed `number | string` and
 * coerced with Number() before formatting.
 */
interface FundraisingDashboardResponse {
  pipeline: Record<string, number>;
  applications: {
    in_flight: number;
    submitted: number;
    awarded: number;
    pending_ask: number | string;
    awarded_ytd: number | string;
  };
  donors: {
    total_donors: number;
    active_12m: number;
    major_prospects: number;
    lifetime_total: number | string;
  };
  members: { active: number; expired_unmarked: number; expiring_30d: number };
  sponsors: { active_sponsors: number; active_value: number | string };
  upcoming: Array<{ id: string; kind: string; title: string; due_at: string; status: string }>;
}

export default function FundraisingPipelineWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'fundraising', 'dashboard'],
    queryFn: () => api.get<FundraisingDashboardResponse>('/admin/fundraising/dashboard'),
    staleTime: 60000,
  });

  return (
    <WidgetShell
      title="Fundraising Pipeline"
      icon={<TrendingUp />}
      to="/admin/fundraising"
      isLoading={isLoading}
      error={error}
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title="In-flight apps"
            value={data.applications.in_flight}
            icon={<ClipboardList />}
            tone="amber"
            to="/admin/fundraising/applications"
          />
          <StatCard
            title="Pending ask"
            value={formatCurrency(Number(data.applications.pending_ask))}
            icon={<Coins />}
            tone="orange"
          />
          <StatCard
            title="Awarded YTD"
            value={formatCurrency(Number(data.applications.awarded_ytd))}
            icon={<Award />}
            tone="green"
          />
          <StatCard
            title="Total donors"
            value={data.donors.total_donors}
            icon={<Users />}
            tone="purple"
            to="/admin/fundraising/donors"
          />
        </div>
      )}
    </WidgetShell>
  );
}
